/**
 * Ontology Wizard — 4-step HITL flow inspired by Protégé / NeOn methodology.
 *
 *   1. Describe use case   → POST /api/v1/ontology/scope/        (scoping agent)
 *   2. Review scope + CQs  → user edits in_scope / out_of_scope / key_terms /
 *                            suggested_standards / competency_questions
 *   3. Build               → POST /api/v1/ontology/scopes/<id>/approve/
 *                            (runs the builder, attaches CQs to the new version)
 *   4. Validate            → links to /ontology with the new version drawer open;
 *                            user can run reasoner, auto-SHACL, CQs, SPARQL,
 *                            and export the artifact.
 *
 * No decorative panels — every control posts a real request and surfaces
 * the response. Disabled states gate forward progress on the previous step's
 * success.
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, ChevronLeft, ChevronRight, ListChecks, Plus, Trash2, Wand2 } from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { ErrorBox, PageHeader } from "@/components/common";

type Cq = { text: string; expected_kind: "nonempty" | "ask" | "any"; sparql?: string };

interface Scope {
  id: string;
  use_case: string;
  industry: string;
  in_scope: string[];
  out_of_scope: string[];
  key_terms: string[];
  suggested_standards: string[];
  competency_questions: Cq[];
  agent_notes: string;
  status: string;
  built_version: string | null;
}

interface BuildResp extends Scope { built_version: string }

const INDUSTRIES = [
  "generic", "finance", "insurance", "healthcare", "legal",
  "retail", "manufacturing", "saas", "telecom", "energy",
];

const MODELS = [
  { id: "stub", label: "stub (deterministic, free)" },
  { id: "openai:gpt-4o-mini", label: "openai:gpt-4o-mini" },
  { id: "openai:gpt-4o",       label: "openai:gpt-4o" },
  { id: "anthropic:claude-3-5-sonnet", label: "anthropic:claude-3-5-sonnet" },
];

export function OntologyNew() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, isLoading: roleLoading, error: roleError } = useWorkspacePermissions();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [useCase, setUseCase] = useState("");
  const [industry, setIndustry] = useState("");
  const [model, setModel] = useState("stub");

  // Step 2 (server-returned, then editable)
  const [scope, setScope] = useState<Scope | null>(null);

  const propose = useMutation({
    mutationFn: () =>
      rawFetch<Scope>("/ontology/scope/", {
        method: "POST",
        body: JSON.stringify({
          workspace: ws,
          use_case: useCase,
          industry: industry || undefined,
          model,
        }),
      }),
    onSuccess: (s) => { setScope(s); setStep(2); },
  });

  const build = useMutation({
    mutationFn: () => {
      if (!scope) throw new Error("no scope");
      return rawFetch<BuildResp>(`/ontology/scopes/${scope.id}/approve/`, {
        method: "POST",
        body: JSON.stringify({
          industry: scope.industry,
          in_scope: scope.in_scope,
          out_of_scope: scope.out_of_scope,
          key_terms: scope.key_terms,
          suggested_standards: scope.suggested_standards,
          competency_questions: scope.competency_questions,
          model,
        }),
      });
    },
    onSuccess: (s) => { setScope(s); setStep(4); },
  });

  if (!canEdit) {
    return <>
      <PageHeader title="New ontology — guided wizard" subtitle="Workspace-scoped ontology authoring."
                  actions={<Link to="/ontology" className="btn">Back to Ontology</Link>} />
      <div className="card text-sm text-muted" role="status">
        {roleLoading ? "Checking your workspace role…" : roleError ?
          "Unable to verify your workspace role. Editing is unavailable." :
          "A workspace editor or admin is required to create an ontology draft."}
      </div>
    </>;
  }

  return (
    <>
      <PageHeader
        title="New ontology — guided wizard"
        subtitle="Scope first, build second. The scoping agent drafts a brief; you approve it; the builder runs against the approved scope and attaches your competency questions to the resulting draft."
        actions={<Link to="/ontology" className="btn">← Back to Ontology</Link>}
      />

      {!ws && (
        <div className="card mb-4 text-sm">
          Pick a workspace from the header dropdown first.
        </div>
      )}

      <Stepper step={step} />

      {step === 1 && (
        <Step1
          useCase={useCase} setUseCase={setUseCase}
          industry={industry} setIndustry={setIndustry}
          model={model} setModel={setModel}
          onNext={() => propose.mutate()}
          isPending={propose.isPending}
          error={propose.error}
          wsReady={Boolean(ws)}
        />
      )}

      {step === 2 && scope && (
        <Step2 scope={scope} setScope={setScope}
               onBack={() => setStep(1)}
               onNext={() => setStep(3)} />
      )}

      {step === 3 && scope && (
        <Step3 scope={scope} model={model}
               onBack={() => setStep(2)}
               onBuild={() => build.mutate()}
               isPending={build.isPending}
               error={build.error} />
      )}

      {step === 4 && scope && scope.built_version && (
        <Step4 versionId={scope.built_version}
               onValidate={() => {
                 sessionStorage.setItem("ckos.ontology.openValidate", scope.built_version!);
                 navigate({ to: "/ontology" });
               }}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------------------- //
// Stepper                                                                     //
// --------------------------------------------------------------------------- //

function Stepper({ step }: { step: number }) {
  const labels = ["Describe", "Review scope", "Build", "Validate"];
  return (
    <div className="card mb-4 flex items-center gap-2 text-xs">
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={l} className="flex flex-1 items-center gap-2">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${
              done ? "border-good bg-good/20 text-good" :
              active ? "border-accent text-accent" :
              "border-border text-muted"
            }`}>
              {done ? <CheckCircle2 size={12} /> : n}
            </span>
            <span className={active ? "font-medium" : "text-muted"}>{l}</span>
            {i < labels.length - 1 && <span className="flex-1 border-t border-dashed border-border" />}
          </div>
        );
      })}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Step 1 — describe                                                           //
// --------------------------------------------------------------------------- //

function Step1({
  useCase, setUseCase, industry, setIndustry, model, setModel,
  onNext, isPending, error, wsReady,
}: {
  useCase: string; setUseCase: (s: string) => void;
  industry: string; setIndustry: (s: string) => void;
  model: string; setModel: (s: string) => void;
  onNext: () => void;
  isPending: boolean; error: unknown; wsReady: boolean;
}) {
  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2">
        <Wand2 size={16} className="text-accent" />
        <h3 className="text-sm font-semibold">1. Describe your use case</h3>
      </div>
      <p className="text-xs text-muted">
        The scoping agent will infer the industry, propose what's in/out of scope,
        suggest seed standards, and draft 6–12 competency questions the ontology
        must be able to answer.
      </p>
      <label className="block">
        <div className="mb-1 text-xs uppercase text-muted">Business use case</div>
        <textarea
          className="input min-h-[140px] font-mono text-xs"
          placeholder="e.g. We process auto-insurance FNOLs end to end: a claimant files, an adjuster is assigned, reserves are set, payments issued, and reinsurance ceded for losses above $1M."
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
        />
      </label>
      <div className="flex gap-3">
        <label className="block flex-1">
          <div className="mb-1 text-xs uppercase text-muted">Industry hint (optional)</div>
          <select className="input text-xs"
                  value={industry} onChange={(e) => setIndustry(e.target.value)}>
            <option value="">auto-detect</option>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <label className="block flex-1">
          <div className="mb-1 text-xs uppercase text-muted">Model</div>
          <select className="input text-xs"
                  value={model} onChange={(e) => setModel(e.target.value)}>
            {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <div className="flex justify-end">
        <button className="btn-primary"
                disabled={!wsReady || isPending || !useCase.trim()}
                onClick={onNext}>
          {isPending ? "Scoping agent thinking…" : <>Run scoping agent <ChevronRight size={14} /></>}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Step 2 — review                                                             //
// --------------------------------------------------------------------------- //

function StringList({
  label, items, onChange,
}: { label: string; items: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="mb-1 text-xs uppercase text-muted">{label}</div>
      <ul className="mb-2 space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            <span className="flex-1 rounded border border-border bg-bg/40 px-2 py-1">{it}</span>
            <button type="button" className="btn-ghost p-1"
                    onClick={() => onChange(items.filter((_, j) => j !== i))}
                    aria-label={`Remove ${it}`}>
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {items.length === 0 && <li className="text-[11px] text-muted">— empty —</li>}
      </ul>
      <div className="flex gap-2">
        <input className="input flex-1 text-xs"
               value={draft}
               onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === "Enter" && draft.trim()) {
                   e.preventDefault();
                   onChange([...items, draft.trim()]);
                   setDraft("");
                 }
               }}
               placeholder={`Add to ${label.toLowerCase()}…`} />
        <button type="button" className="btn"
                onClick={() => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft(""); } }}>
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}

function CqEditor({
  cqs, onChange,
}: { cqs: Cq[]; onChange: (next: Cq[]) => void }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-xs uppercase text-muted">
        <ListChecks size={12} /> Competency questions
      </div>
      <ul className="mb-2 space-y-1">
        {cqs.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="flex-1 rounded border border-border bg-bg/40 px-2 py-1">
              {c.text}
              <span className="ml-2 font-mono text-[10px] text-muted">[{c.expected_kind}]</span>
            </span>
            <select
              className="input w-[7rem] text-[11px]"
              value={c.expected_kind}
              onChange={(e) => {
                const next = cqs.slice();
                next[i] = { ...c, expected_kind: e.target.value as Cq["expected_kind"] };
                onChange(next);
              }}
            >
              <option value="nonempty">non-empty</option>
              <option value="ask">ASK</option>
              <option value="any">any</option>
            </select>
            <button type="button" className="btn-ghost p-1"
                    onClick={() => onChange(cqs.filter((_, j) => j !== i))}>
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {cqs.length === 0 && <li className="text-[11px] text-muted">— no CQs yet —</li>}
      </ul>
      <div className="flex gap-2">
        <input className="input flex-1 text-xs"
               value={draft}
               onChange={(e) => setDraft(e.target.value)}
               placeholder="e.g. Which Policies are ceded under at least one Treaty?"
               onKeyDown={(e) => {
                 if (e.key === "Enter" && draft.trim()) {
                   e.preventDefault();
                   onChange([...cqs, { text: draft.trim(), expected_kind: "nonempty" }]);
                   setDraft("");
                 }
               }} />
        <button type="button" className="btn"
                onClick={() => { if (draft.trim()) { onChange([...cqs, { text: draft.trim(), expected_kind: "nonempty" }]); setDraft(""); } }}>
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}

function Step2({
  scope, setScope, onBack, onNext,
}: { scope: Scope; setScope: (s: Scope) => void; onBack: () => void; onNext: () => void }) {
  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">2. Review &amp; edit the scope</h3>
        <span className="pill-warn">{scope.status}</span>
      </div>
      {scope.agent_notes && (
        <div className="rounded border border-border bg-bg/40 p-2 text-xs text-muted">
          <span className="font-semibold text-fg">Agent notes:</span> {scope.agent_notes}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Industry</div>
          <select className="input text-xs"
                  value={scope.industry}
                  onChange={(e) => setScope({ ...scope, industry: e.target.value })}>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            {!INDUSTRIES.includes(scope.industry) && scope.industry && (
              <option value={scope.industry}>{scope.industry}</option>
            )}
          </select>
        </label>
        <div>
          <div className="mb-1 text-xs uppercase text-muted">Suggested seed standards</div>
          <div className="flex flex-wrap gap-1 text-xs">
            {(scope.suggested_standards || []).length === 0 && <span className="text-muted">— none —</span>}
            {scope.suggested_standards.map((s) => (
              <span key={s} className="pill-good">{s}</span>
            ))}
          </div>
        </div>
        <StringList label="In scope" items={scope.in_scope}
                    onChange={(v) => setScope({ ...scope, in_scope: v })} />
        <StringList label="Out of scope" items={scope.out_of_scope}
                    onChange={(v) => setScope({ ...scope, out_of_scope: v })} />
        <StringList label="Key terms (candidate classes)" items={scope.key_terms}
                    onChange={(v) => setScope({ ...scope, key_terms: v })} />
        <div /> {/* spacer */}
      </div>
      <CqEditor cqs={scope.competency_questions}
                onChange={(v) => setScope({ ...scope, competency_questions: v })} />
      <div className="flex justify-between">
        <button className="btn" onClick={onBack}>
          <ChevronLeft size={14} /> Back
        </button>
        <button className="btn-primary"
                disabled={scope.in_scope.length === 0 || scope.competency_questions.length === 0}
                onClick={onNext}>
          Approve scope <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Step 3 — build                                                              //
// --------------------------------------------------------------------------- //

function Step3({
  scope, model, onBack, onBuild, isPending, error,
}: {
  scope: Scope; model: string; onBack: () => void; onBuild: () => void;
  isPending: boolean; error: unknown;
}) {
  return (
    <div className="card space-y-3">
      <h3 className="text-sm font-semibold">3. Build the draft ontology</h3>
      <p className="text-xs text-muted">
        The builder will receive the use case, the in-scope items, the out-of-scope
        items, and the key terms. It returns a DRAFT version (never auto-published)
        with your {scope.competency_questions.length} CQs attached.
      </p>
      <div className="rounded border border-border bg-bg/40 p-3 text-xs">
        <div><span className="text-muted">Industry:</span> <code>{scope.industry || "(auto)"}</code></div>
        <div><span className="text-muted">Model:</span> <code>{model}</code></div>
        <div><span className="text-muted">In scope:</span> {scope.in_scope.length}</div>
        <div><span className="text-muted">Out of scope:</span> {scope.out_of_scope.length}</div>
        <div><span className="text-muted">Key terms:</span> {scope.key_terms.length}</div>
        <div><span className="text-muted">CQs:</span> {scope.competency_questions.length}</div>
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <div className="flex justify-between">
        <button className="btn" onClick={onBack} disabled={isPending}>
          <ChevronLeft size={14} /> Back
        </button>
        <button className="btn-primary" onClick={onBuild} disabled={isPending}>
          {isPending ? "Builder running…" : "Build draft ontology"}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Step 4 — validate                                                           //
// --------------------------------------------------------------------------- //

function Step4({ versionId, onValidate }: { versionId: string; onValidate: () => void }) {
  return (
    <div className="card space-y-3">
      <h3 className="text-sm font-semibold">4. Draft created — validate it</h3>
      <p className="text-xs text-muted">
        Open the version's validation drawer to run the OWL-RL reasoner, generate
        SHACL shapes from cardinalities, run your competency questions, query the
        TBox via SPARQL, and export TTL / JSON-LD / OWL / Cypher / CSV.
      </p>
      <div className="rounded border border-border bg-bg/40 p-3 text-xs">
        <div><span className="text-muted">Version ID:</span> <code className="font-mono">{versionId}</code></div>
      </div>
      <div className="flex justify-end">
        <button className="btn-primary" onClick={onValidate}>
          Open validation tools <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
